import React from 'react';
import {
  Alert,
  Box,
  Divider,
  Typography,
} from '@mui/material';
import type { LearningLabNavState } from './learningLabNavVisibility';
import { getLearningLabNavStatusMessage } from './learningLabNavVisibility';
import { LearningLabMenuSection } from './LearningLabMenuSection';
import { getAvailableLearningLabOfferings } from './learningLabOfferings';
import type { LearningLabNavContext } from './learningLabNavVisibility';

export interface LearningLabNavPopoverProps {
  navState: LearningLabNavState;
  navContext: LearningLabNavContext;
  onClose: () => void;
  /** When true (default), show a soft desktop-recommended tip on narrow viewports. */
  showDesktopTip?: boolean;
}

export const LearningLabNavPopover: React.FC<LearningLabNavPopoverProps> = ({
  navState,
  navContext,
  onClose,
  showDesktopTip = true,
}) => {
  const offerings = getAvailableLearningLabOfferings(navContext);
  const statusMessage = getLearningLabNavStatusMessage(navState);
  const showActions =
    navState === 'available' ||
    navState === 'active' ||
    navState === 'activeStale';

  return (
    <Box
      data-testid="learning-lab-nav-popover"
      sx={{ width: 320, maxWidth: '90vw', p: 2 }}
    >
      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
        Learning Lab practice
      </Typography>

      {showDesktopTip ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Desktop recommended for form-heavy practice steps.
        </Typography>
      ) : null}

      {statusMessage && !showActions ? (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          {statusMessage}
        </Alert>
      ) : null}

      {statusMessage && showActions ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {statusMessage}
        </Typography>
      ) : null}

      {offerings.length > 0 && showActions ? (
        <>
          <LearningLabMenuSection
            variant="popover"
            navState={navState}
            onActionComplete={onClose}
          />
        </>
      ) : null}

      {offerings.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No learning labs are available for your account.
        </Typography>
      ) : null}

      {!showActions && navState !== 'hidden' ? (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="body2" color="text.secondary">
            {offerings[0]?.description}
          </Typography>
        </>
      ) : null}
    </Box>
  );
};

export default LearningLabNavPopover;
